import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CrmRecord } from '../../lib/api-types';
import { schemaQuery, timelineQuery } from '../../lib/queries';
import { RelatedRecords } from '../crm/RelatedRecords';
import { FieldValue } from '../crm/FieldValue';
import { editableFields, RecordEditor } from '../crm/RecordEditor';
import { Timeline } from '../crm/Timeline';
import styles from './Notes.module.css';

export function NoteSidebar({ record, objectName, canEdit }: {
  record?: CrmRecord | undefined; objectName: string; canEdit: boolean;
}) {
  const [tab, setTab] = useState<'related' | 'metadata'>('related');
  const [objectFilter, setObjectFilter] = useState('');
  const [search, setSearch] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const id = useId();
  const schema = useQuery(schemaQuery());
  const object = schema.data?.objects.find(item => item.namePlural === objectName || item.name === objectName);
  const properties = object && { ...object, fields: object.fields.filter(field => !['title', 'body'].includes(field.name)) };
  const metadata = record?.fields.filter(field => !['title', 'body'].includes(field.name) && field.type !== 'position') ?? [];
  const history = useQuery({ ...timelineQuery({ record: record?.id ?? '', limit: 50 }),
    enabled: Boolean(record) && tab === 'metadata' && historyOpen });

  return <aside className={styles.sidebar} aria-label="Note information">
    <div className={styles.sidebarTabs} role="tablist" aria-label="Note information">
      {(['related', 'metadata'] as const).map((item, index) => <button key={item} type="button" role="tab"
        id={`${id}-${item}`} aria-controls={`${id}-${item}-panel`} aria-selected={tab === item} tabIndex={tab === item ? 0 : -1}
        onClick={() => setTab(item)} onKeyDown={event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 'related' : event.key === 'End' ? 'metadata' : index === 0 ? 'metadata' : 'related';
          setTab(next); document.getElementById(`${id}-${next}`)?.focus();
        }}>{item === 'related' ? 'Related' : 'Metadata'}</button>)}
    </div>
    <div className={styles.sidebarContent} role="tabpanel" id={`${id}-related-panel`} aria-labelledby={`${id}-related`} hidden={tab !== 'related'}>
      <RelatedRecords references={record?.relations ?? []} objectFilter={objectFilter} onObjectFilter={setObjectFilter} search={search} onSearch={setSearch} />
      {!record && <p className={styles.sidebarHint}>Save your note to connect it to other records.</p>}
    </div>
    <div className={styles.sidebarContent} role="tabpanel" id={`${id}-metadata-panel`} aria-labelledby={`${id}-metadata`} hidden={tab !== 'metadata'}>
      {record ? <>
        <dl className={styles.metadata}>
          <div><dt>Created</dt><dd><MetadataDate value={record.createdAt} /></dd></div>
          <div><dt>Last edited</dt><dd><MetadataDate value={record.updatedAt} /></dd></div>
          {metadata.map(field => <div key={field.id}><dt>{field.label}</dt><dd><FieldValue field={field} /></dd></div>)}
        </dl>
        {properties && editableFields(properties).length > 0 && <button type="button" className={styles.quietButton} disabled={!canEdit} onClick={() => setEditing(true)}>Edit properties</button>}
        <details className={styles.noteHistory} open={historyOpen} onToggle={event => setHistoryOpen(event.currentTarget.open)}>
          <summary>History</summary>
          {historyOpen && (history.isLoading ? <p className={styles.sidebarHint}>Loading history…</p>
            : history.isError ? <p role="alert">History could not be loaded. <button type="button" onClick={() => void history.refetch()}>Try again</button></p>
            : <Timeline events={history.data?.events ?? []} />)}
        </details>
      </> : <p className={styles.sidebarHint}>Note details will appear here after saving.</p>}
    </div>
    {editing && properties && record && <RecordEditor object={properties} version={schema.data?.schemaVersion ?? 0} record={record}
      onClose={() => setEditing(false)} onSaved={() => setEditing(false)} />}
  </aside>;
}

function MetadataDate({ value }: { value: string }) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? <>—</> : <time dateTime={value}>{date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</time>;
}
